import { useRef, useState } from 'react';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { postSourcingSearch } from 'services/sourcingSearch/api';
import { mapSourcingSearchResponse } from 'modules/candidate-profile/tables/filters/mapSourcingSearchResponse';
import {
  ExplanationContext,
  SourcingSearchRequest,
  SourcingSearchResponse
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { CandidateSearchResult } from 'modules/candidate-profile/types/CandidateSearchResult';

type SearchCache = {
  // Switching organization is a soft switch, and sessionStorage survives it.
  organizationId?: string;
  // A reload is a new page load, and sessionStorage survives that too.
  pageLoadId?: string;
  input: SourcingSearchRequest;
  data: CandidateSearchResult;
  explanationContext?: ExplanationContext | null;
  // The candidates a "people like X" search ranks against. The endpoint calls
  // them likeCandidateNames.
  similarCandidateNames?: string[];
};

// What the calls that follow a search -- explanations, filter-only re-runs --
// need from it.
type FollowUpInputs = Pick<
  SearchCache,
  'explanationContext' | 'similarCandidateNames'
>;

const VECTOR_SEARCH_CACHE_KEY = 'vector-search-cache';

const PAGE_LOAD_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// An open tab can hold an entry from the build that sent the old query shape.
const isSourcingRequest = (input: unknown): input is SourcingSearchRequest =>
  Boolean(input) && typeof input === 'object' && 'availableFilters' in input!;

const toExplanationContext = (
  response: SourcingSearchResponse,
  recruiterMessage?: string | null
): ExplanationContext | null => {
  if (!response.id || !response.generatedSemanticQuery) return null;

  return {
    searchId: response.id,
    semanticQuery: response.generatedSemanticQuery,
    recruiterMessage: recruiterMessage ?? null
  };
};

const getInitialCache = (organizationId: string): SearchCache | null => {
  const cached = sessionStorage.getItem(VECTOR_SEARCH_CACHE_KEY);
  if (!cached) return null;

  const parsed = JSON.parse(cached) as SearchCache;

  if (parsed.organizationId !== organizationId) return null;
  if (!isSourcingRequest(parsed.input)) return null;

  // A message left by an older build would write a second turn on refresh.
  const input = { ...parsed.input } as Record<string, unknown>;
  delete input.messageBody;

  return { ...parsed, input: input as SourcingSearchRequest };
};

export const useVectorSearchCache = () => {
  const organization = useOrganization();

  // One read, so the entry and the flag below cannot disagree.
  const [restoredCache] = useState(() => getInitialCache(organization.id));
  const [searchCache, setSearchCache] = useState<SearchCache | null>(
    restoredCache
  );
  // A restored entry counts as already searched, so the table shows it.
  const [hasSearched, setHasSearched] = useState(restoredCache !== null);
  const [isSearching, setIsSearching] = useState(false);
  // A restored entry may belong to another list, such as the plain list
  // opened by URL in the same tab. Its explanations wait until a request is
  // served from it.
  const [isRestoreConfirmed, setIsRestoreConfirmed] = useState(
    restoredCache === null
  );
  // Network activity on this mount; a restore sets hasSearched without one.
  const hasFetchedRef = useRef(false);
  // Only the newest request's response may set the rows.
  const latestSendRef = useRef(0);

  const fetchAndCacheSearch = async (
    request: SourcingSearchRequest,
    followUpInputsFor: (response: SourcingSearchResponse) => FollowUpInputs
  ) => {
    hasFetchedRef.current = true;
    latestSendRef.current += 1;
    const sendId = latestSendRef.current;
    const isLatest = () => sendId === latestSendRef.current;
    setIsSearching(true);
    try {
      const response = await postSourcingSearch(request);

      // A refused query never searched; its empty result would clear the table.
      if (response.bias?.query_is_biased) {
        return { data: searchCache?.data, response };
      }

      const data = mapSourcingSearchResponse(response);
      if (!isLatest()) return { data, response, superseded: true };

      // Refreshed from, not replayed: re-sending the message writes a turn.
      const input = { ...request };
      delete input.messageBody;
      input.id = response.id ?? request.id;

      const next: SearchCache = {
        organizationId: organization.id,
        pageLoadId: PAGE_LOAD_ID,
        input,
        data,
        ...followUpInputsFor(response)
      };
      setSearchCache(next);
      setHasSearched(true);
      setIsRestoreConfirmed(true);
      sessionStorage.setItem(VECTOR_SEARCH_CACHE_KEY, JSON.stringify(next));

      return { data, response };
    } finally {
      if (isLatest()) setIsSearching(false);
    }
  };

  const servesFromCache = (request: SourcingSearchRequest) => {
    if (searchCache?.pageLoadId !== PAGE_LOAD_ID) return false;
    if (JSON.stringify(searchCache.input) === JSON.stringify(request))
      return true;

    // The chat stored it with a message, the table asks for it plainly, so
    // only the id matches them. Never with a filter: the id says nothing
    // about whether the stored rows still pass it.
    return (
      Boolean(request.id) &&
      !request.filter &&
      searchCache.input.id === request.id
    );
  };

  const handleSourcingSearch = async (
    request: SourcingSearchRequest
  ): Promise<{
    data: CandidateSearchResult | undefined;
    response: SourcingSearchResponse | undefined;
    superseded?: boolean;
  }> => {
    // Read only until this mount fetches: after that a repeated request is
    // the user asking again, and the stored rows may be minutes stale. A
    // message never hits the cache -- its reply comes back with it.
    const cacheable = !request.messageBody && !hasFetchedRef.current;

    if (cacheable && servesFromCache(request)) {
      setHasSearched(true);
      setIsRestoreConfirmed(true);
      return { data: searchCache?.data, response: undefined };
    }

    const previous = searchCache?.explanationContext;

    return fetchAndCacheSearch(request, (response) => ({
      // A filter-only re-run of the same search keeps the turn's message.
      // Without it the same rows get a new explanations request, and the LLM
      // rewords them.
      explanationContext: toExplanationContext(
        response,
        request.messageBody ??
          (previous?.searchId === response.id
            ? previous?.recruiterMessage
            : null)
      ),
      // A filter-only re-run keeps the previous turn's similar candidates.
      similarCandidateNames: request.messageBody
        ? response.likeCandidateNames ?? []
        : searchCache?.similarCandidateNames ?? []
    }));
  };

  const refetchVectorSearch = async () => {
    if (!searchCache?.input) return { data: null };

    const { data } = await fetchAndCacheSearch(
      searchCache.input,
      (response) => ({
        explanationContext: toExplanationContext(
          response,
          searchCache.explanationContext?.recruiterMessage ?? undefined
        ),
        similarCandidateNames: searchCache.similarCandidateNames
      })
    );

    return { data };
  };

  // The stored rows stand for this request too, such as the chips a chat turn
  // merged in. A reload builds it, and must find the rows rather than search.
  // An updater, because the caller can hold a render from before the search.
  const associateCachedResultWithRequest = (request: SourcingSearchRequest) => {
    setSearchCache((current) => {
      if (!current) return current;

      const next = { ...current, input: request };
      // Idempotent, so a repeated updater call is harmless.
      sessionStorage.setItem(VECTOR_SEARCH_CACHE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const clearSearchCache = () => {
    setSearchCache(null);
    setHasSearched(false);
    sessionStorage.removeItem(VECTOR_SEARCH_CACHE_KEY);
  };

  return {
    // Withheld until searched, or another list's result shows on arrival.
    data: hasSearched ? searchCache?.data : undefined,
    explanationContext:
      hasSearched && isRestoreConfirmed
        ? searchCache?.explanationContext ?? null
        : null,
    similarCandidateNames: hasSearched
      ? searchCache?.similarCandidateNames ?? []
      : [],
    loading: isSearching,
    handleSourcingSearch,
    refetchVectorSearch,
    associateCachedResultWithRequest,
    clearSearchCache
  };
};
